import React, { useState } from "react";

// Permission keys für jeden Tab basierend auf Gerätetyp
const getPermissionKey = (tabId, deviceType) => {
  if (deviceType === "desktop") return null; // Desktop braucht keine mobile Berechtigungen
  
  const baseMap = {
    "children": "children",
    "attendance": "attendance",
    "stats": "stats",
    "email": "email",
    "course-sessions": "course_sessions",
    "groups": "groups",
    "help": null, // Hilfe immer sichtbar
  };
  
  const base = baseMap[tabId];
  if (base === null) return null;
  
  return `${deviceType}_${base}`; // z.B. "smartphone_children" oder "tablet_attendance"
};

const NAV_ITEMS = [
  { id: "children", label: "Kinder", roles: ["superadmin", "admin", "trainer"], permission: "children_view" },
  { id: "attendance", label: "Anwesenheit", roles: ["superadmin", "admin", "trainer", "mitarbeiter"], permission: "attendance_view" },
  { id: "stats", label: "Auswertung", roles: ["superadmin", "admin", "trainer"], permission: "stats_view" },
  { id: "email", label: "E-Mail", roles: ["superadmin", "admin", "trainer"], permission: "email_send" },
  { id: "course-sessions", label: "Kurstermine", roles: ["superadmin", "admin"], permission: "course_sessions_view" },
  { id: "courses", label: "Kursbuchung", roles: ["superadmin", "admin"] },
  { id: "groups", label: "Gruppen", roles: ["superadmin", "admin"], permission: "groups_view" },
  { id: "tickets", label: "Tickets", roles: ["superadmin", "admin", "trainer", "mitarbeiter"], permission: "tickets_create" },
  { id: "help", label: "Hilfe", roles: ["superadmin", "admin", "trainer", "mitarbeiter"] },
];

const SETTINGS_ITEMS = [
  { id: "account", label: "Konto" },
  { id: "users", label: "Benutzer" },
  { id: "permissions", label: "Berechtigungen" },
  { id: "system", label: "System" },
];

export function ModernNavigation({ 
  selectedTab, 
  setSelectedTab, 
  settingsTab, 
  setSettingsTab,
  userRole,
  deviceType = "desktop", // "smartphone", "tablet", oder "desktop"
  permissions = {}
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  const isSuperAdmin = userRole === "superadmin";
  const isMobile = deviceType === "smartphone" || deviceType === "tablet";
  
  // Filter Nav-Items basierend auf Rolle UND Berechtigungen
  const visibleNavItems = NAV_ITEMS.filter(item => {
    // SuperAdmin hat immer Zugriff auf alles
    if (isSuperAdmin) return true;
    
    // Rolle prüfen (als Basis)
    if (!item.roles.includes(userRole)) return false;
    
    // Feature-Berechtigung prüfen (z.B. email_send für E-Mail-Tab)
    if (item.permission && permissions[item.permission] !== true) {
      return false;
    }
    
    // Geräte-Berechtigung prüfen (nur bei Smartphone/Tablet)
    if (isMobile) {
      const permKey = getPermissionKey(item.id, deviceType);
      // Wenn kein Permission-Key definiert (z.B. "help"), immer anzeigen
      if (permKey === null) return true;
      // Sonst Permission prüfen
      return permissions[permKey] === true;
    }
    
    return true;
  });

  const handleNavClick = (id) => {
    setSelectedTab(id);
    setSettingsOpen(false);
  };

  const handleSettingsClick = (settingId) => {
    setSelectedTab("account");
    setSettingsTab(settingId);
    setSettingsOpen(false);
  };

  if (isMobile) {
    // Mobile: Kompakte Navigation mit horizontalem Scroll
    return (
      <nav 
        className="rounded-xl bg-white p-1.5 shadow-sm" 
        data-testid="modern-nav"
      >
        <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-thin">
          {visibleNavItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => handleNavClick(item.id)}
              className={`flex-shrink-0 whitespace-nowrap rounded-lg px-3 py-2 transition-all ${
                selectedTab === item.id
                  ? "shadow-sm"
                  : "text-slate-700 hover:bg-slate-100"
              }`}
              style={{ 
                fontSize: 'var(--font-nav-size)', 
                fontWeight: 'var(--font-nav-weight)',
                fontFamily: 'var(--font-primary)',
                ...(selectedTab === item.id ? {
                  backgroundColor: 'var(--nav-active-bg)',
                  color: 'var(--nav-active-text)'
                } : {})
              }}
            >
              {item.label}
            </button>
          ))}
          
          {/* Settings Button für Super-Admin auf Mobile */}
          {isSuperAdmin && (
            <div className="relative flex-shrink-0">
              <button
                data-testid="nav-settings-dropdown-mobile"
                onClick={() => setSettingsOpen(!settingsOpen)}
                className={`flex items-center gap-1 rounded-lg px-3 py-2 transition-all whitespace-nowrap ${
                  selectedTab === "account"
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
                style={{ 
                  fontSize: 'var(--font-nav-size)', 
                  fontWeight: 'var(--font-nav-weight)',
                  fontFamily: 'var(--font-primary)'
                }}
              >
                Einstellungen
                <svg 
                  className={`h-3 w-3 transition-transform flex-shrink-0 ${settingsOpen ? "rotate-180" : ""}`} 
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {settingsOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-xl">
                  <div className="p-1">
                    {SETTINGS_ITEMS.map((item) => (
                      <button
                        key={item.id}
                        data-testid={`nav-settings-${item.id}-mobile`}
                        onClick={() => handleSettingsClick(item.id)}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all ${
                          selectedTab === "account" && settingsTab === item.id
                            ? ""
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                        style={{ 
                          fontSize: 'var(--font-body-size)', 
                          fontWeight: 'var(--font-body-weight)',
                          fontFamily: 'var(--font-primary)',
                          ...(selectedTab === "account" && settingsTab === item.id ? {
                            backgroundColor: 'var(--nav-hover-bg)',
                            color: 'var(--nav-hover-text)'
                          } : {})
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Scroll-Hinweis für mehr Tabs */}
        {visibleNavItems.length > 4 && (
          <div className="flex justify-center pt-1">
            <div className="flex gap-1">
              {visibleNavItems.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 w-1 rounded-full ${idx < 4 ? 'bg-slate-300' : 'bg-slate-200'}`} 
                />
              ))}
            </div>
          </div>
        )}
      </nav>
    );
  }

  return (
    <nav className="flex items-center justify-between rounded-xl bg-white px-2 py-1.5 shadow-sm" data-testid="modern-nav">
      {/* Haupt-Navigation */}
      <div className="flex items-center gap-1 flex-wrap">
        {visibleNavItems.map((item) => (
          <button
            key={item.id}
            data-testid={`nav-${item.id}`}
            onClick={() => handleNavClick(item.id)}
            className={`rounded-lg px-4 py-2 transition-all whitespace-nowrap ${
              selectedTab === item.id
                ? "shadow-sm"
                : "text-slate-700 hover:bg-slate-100"
            }`}
            style={{ 
              fontSize: 'var(--font-nav-size)', 
              fontWeight: 'var(--font-nav-weight)',
              fontFamily: 'var(--font-primary)',
              ...(selectedTab === item.id ? {
                backgroundColor: 'var(--nav-active-bg)',
                color: 'var(--nav-active-text)'
              } : {})
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Settings Dropdown (nur für Super-Admin) */}
      {isSuperAdmin && (
        <div className="relative flex-shrink-0">
          <button
            data-testid="nav-settings-dropdown"
            onClick={() => setSettingsOpen(!settingsOpen)}
            onBlur={() => setTimeout(() => setSettingsOpen(false), 200)}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 transition-all whitespace-nowrap ${
              selectedTab === "account"
                ? "bg-slate-900 text-white"
                : "text-slate-700 hover:bg-slate-100"
            }`}
            style={{ 
              fontSize: 'var(--font-nav-size)', 
              fontWeight: 'var(--font-nav-weight)',
              fontFamily: 'var(--font-primary)'
            }}
          >
            Einstellungen
            <svg 
              className={`h-3 w-3 transition-transform flex-shrink-0 ${settingsOpen ? "rotate-180" : ""}`} 
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {settingsOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-xl">
              <div className="p-1">
                {SETTINGS_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    data-testid={`nav-settings-${item.id}`}
                    onClick={() => handleSettingsClick(item.id)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all ${
                      selectedTab === "account" && settingsTab === item.id
                        ? ""
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                    style={{ 
                      fontSize: 'var(--font-body-size)', 
                      fontWeight: 'var(--font-body-weight)',
                      fontFamily: 'var(--font-primary)',
                      ...(selectedTab === "account" && settingsTab === item.id ? {
                        backgroundColor: 'var(--nav-hover-bg)',
                        color: 'var(--nav-hover-text)'
                      } : {})
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
